import { AfterContentInit, Directive, ElementRef, EventEmitter, HostListener, inject, Input, Output } from '@angular/core';
/**
 * this is a A11Y helper directive for converting links without href and only a (click) handler (not focusable, no pointer cursor, do not react on ENTER key...)
 * into more native and A11Y friendly links
 *
 * Example:
 *  `<a (click)="doSomething()">foo</a>` can be converted to
 *  `<a (igA11yLink)="doSomething()">foo</a>` and is then A11Y friendly
 */

export type A11yClickEvent = Event & {
  target: EventTarget | null,
  clientX: number,
  clientY: number,
  pageX: number,
  pageY: number,
}

@Directive({
  standalone: true,
  selector: '[igA11yLink]',
})
export class A11yLinkDirective implements AfterContentInit {
  readonly #el = inject(ElementRef);

  /**
   * set this to false if the directive should not add tabIndex and class.
   * Useful if you have conditions where the element should not be focusable but leaving away the (igA11yLink) directive on a condition is too much work
   */
  @Input() igA11yLinkVisible = true;

  @HostListener('click', ['$event'])
  onClick(e: MouseEvent) {
    this.igA11yLink.emit(e);
  }

  @HostListener('keydown', ['$event'])
  onEnter(e: KeyboardEvent) {
    if ((this.#el.nativeElement as HTMLElement).tagName === 'BUTTON' && e.code === 'Space') {
      // let the browser handle space on buttons natively!
      return;
    }


    if ((e.code === 'Enter' || e.code === 'Space') && document.activeElement === this.#el.nativeElement) {
      e.preventDefault();
      this.igA11yLink.emit({
        ...e,
        preventDefault: () => e.preventDefault(),
        stopPropagation: () => e.stopPropagation(),
        currentTarget: this.#el.nativeElement,
        target: this.#el.nativeElement,
        clientY: this.#el.nativeElement.getBoundingClientRect()?.top,
        clientX: this.#el.nativeElement.getBoundingClientRect()?.left,
        pageY: this.#el.nativeElement.getBoundingClientRect()?.top + window.scrollY,
        pageX: this.#el.nativeElement.getBoundingClientRect()?.left + window.scrollX,
      });
    }
  }

  /**
   * emits on click AND keydown.enter events
   */
  @Output() igA11yLink = new EventEmitter<A11yClickEvent>();

  ngAfterContentInit() {
    // always add a tabindex so link is focusable
    if (this.igA11yLinkVisible) {
      (this.#el.nativeElement as HTMLElement).setAttribute('tabindex', '0');

      let currentClass = (this.#el.nativeElement as HTMLElement).getAttribute('class');
      if (!currentClass) {
        currentClass = '';
      }
      (this.#el.nativeElement as HTMLElement).setAttribute('class', (currentClass + ' ig-a11ylink').trim());
    }

  }
}
